import React from 'react'
import { CheckIcon } from '@heroicons/react/24/outline'
import { SubscriptionTier } from '../../types'

interface PricingTierCardProps {
  tier: SubscriptionTier
  billingCycle?: 'monthly' | 'yearly'
  onSelect?: (tier: SubscriptionTier) => void
  onEdit?: (tier: SubscriptionTier) => void
  isCreator?: boolean
  isSelected?: boolean
  currentTier?: boolean
}

const PricingTierCard: React.FC<PricingTierCardProps> = ({
  tier,
  billingCycle = 'monthly',
  onSelect,
  onEdit,
  isCreator = false,
  isSelected = false,
  currentTier = false,
}) => {
  const priceCents =
    billingCycle === 'yearly' && tier.price_yearly_cents != null
      ? tier.price_yearly_cents
      : tier.price_monthly_cents

  return (
    <div
      className={`relative bg-white rounded-lg shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg ${
        isSelected ? 'ring-2 ring-blue-500' : ''
      } ${currentTier ? 'ring-2 ring-green-500' : ''}`}
    >
      {tier.is_featured && (
        <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs font-semibold rounded-bl-lg">
          Popular
        </div>
      )}

      {currentTier && (
        <div className="absolute top-0 left-0 bg-green-500 text-white px-3 py-1 text-xs font-semibold rounded-br-lg">
          Current Plan
        </div>
      )}

      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
        <p className="text-gray-600 text-sm mb-4">{tier.description}</p>

        <div className="mb-6">
          <span className="text-4xl font-bold text-gray-900">
            ${(priceCents / 100).toFixed(2)}
          </span>
          <span className="text-gray-600 text-sm ml-2">
            /{billingCycle === 'yearly' ? 'year' : 'month'}
          </span>
        </div>

        <div className="space-y-3 mb-6">
          <p className="text-sm font-semibold text-gray-700">Features:</p>
          {tier.features.map((feature, index) => (
            <div key={index} className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{feature}</span>
            </div>
          ))}
        </div>

        {isCreator ? (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{tier.current_subscribers}</span> subscribers
            </div>
            <button
              onClick={() => onEdit?.(tier)}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium"
            >
              Edit Tier
            </button>
            <div className="flex justify-between text-xs">
              <span className={tier.is_active ? 'text-green-600' : 'text-gray-500'}>
                {tier.is_active ? 'Active' : 'Inactive'}
              </span>
              {tier.stripe_price_id_monthly ? (
                <span className="text-green-600">Live on Stripe ✓</span>
              ) : (
                <span className="text-amber-700">Not on Stripe yet</span>
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={() => onSelect?.(tier)}
            disabled={currentTier || !tier.is_active}
            className={`w-full px-4 py-2 rounded-md font-medium transition-colors ${
              currentTier
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                : tier.is_active
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-500 cursor-not-allowed'
            }`}
          >
            {currentTier ? 'Current Plan' : tier.is_active ? 'Subscribe' : 'Not Available'}
          </button>
        )}
      </div>
    </div>
  )
}

export default PricingTierCard
